import { useEffect } from 'react';

import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';
import { AUTH_ERROR_MESSAGES } from '@/lib/auth-errors';

/**
 * Single-session guard.
 *
 * When `useAuth` reports status === 'superseded' (which happens when
 * another device or browser logs into the same account), this hook
 * surfaces a destructive toast. The user clears local state via
 * "Sign out" in `SessionGuardBanner` (or signs in again).
 *
 * Mount this once near the top of the authenticated tree. The
 * SessionGuardBanner component pairs with it for a persistent visual
 * indicator.
 */
export function useSessionGuard(): void {
  const { status } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (status !== 'superseded') return;

    toast({
      variant: 'destructive',
      title: 'Session ended',
      description: AUTH_ERROR_MESSAGES.SESSION_SUPERSEDED,
    });
  }, [status, toast]);
}
